import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardAction, CardFooter } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Progress } from '../ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Trophy, TrendingUp, TrendingDown, Users, Star, Loader2 } from 'lucide-react';
import { ResponsiveRadar } from '@nivo/radar';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { advancedReportsAPI } from '../../services/api';

interface TeamMetrics {
  team_id: number;
  team_name: string;
  agent_count: number;
  total_conversations: number;
  conversations_per_agent: number;
  avg_resolution_time: number;
  resolution_rate: number;
  avg_csat: number;
  efficiency_score: number;
  rank: number;
  collaboration_index: number;
  escalation_rate: number;
}

interface TeamComparisonData {
  teams: TeamMetrics[];
  best_performing_team: TeamMetrics;
  benchmarks: {
    avg_efficiency_score: number;
    avg_resolution_time: number;
    avg_csat: number;
  };
  radar_data: Array<{
    metric: string;
    [key: string]: string | number;
  }>;
}

interface TeamPerformanceAnalyticsProps {
  startDate: string;
  endDate: string;
}

const getPerformanceTierVariant = (score: number): 'secondary' | 'outline' | 'destructive' => {
  if (score >= 80) return 'secondary';
  if (score >= 70) return 'outline';
  return 'destructive';
};

const getPerformanceTierLabel = (score: number): string => {
  if (score >= 90) return 'Excellent';
  if (score >= 80) return 'Good';
  if (score >= 70) return 'Average';
  return 'Needs Improvement';
};

const TeamPerformanceAnalytics: React.FC<TeamPerformanceAnalyticsProps> = ({
  startDate,
  endDate
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TeamComparisonData | null>(null);

  useEffect(() => {
    fetchTeamData();
  }, [startDate, endDate]);

  const fetchTeamData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await advancedReportsAPI.getTeamAnalytics({
        start_date: startDate,
        end_date: endDate
      });

      setData(response);
    } catch (err: any) {
      console.error('Failed to fetch team analytics:', err);
      setError(err.message || 'Failed to load team performance data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!data || !data.teams.length) {
    return (
      <Alert>
        <AlertDescription>No team performance data available for the selected period.</AlertDescription>
      </Alert>
    );
  }

  if (!data.best_performing_team) {
    return (
      <Alert>
        <AlertDescription>Team performance data is incomplete. Please ensure teams have sufficient activity in the selected period.</AlertDescription>
      </Alert>
    );
  }

  const barChartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false }
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 6,
        dataLabels: {
          position: 'top'
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val}%`,
      offsetX: -6,
      style: {
        fontSize: '12px',
        colors: ['#fff']
      }
    },
    xaxis: {
      categories: data.teams.map(t => t.team_name),
      max: 100,
      labels: {
        formatter: (val) => `${val}%`
      }
    },
    yaxis: {
      labels: {
        style: {
          fontSize: '12px'
        }
      }
    },
    colors: ['#1f93ff'],
    grid: {
      borderColor: '#e7e7e7'
    },
    tooltip: {
      y: {
        formatter: (val) => `${val.toFixed(1)}%`
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Performer Card */}
      <Card className="bg-muted/30">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <Trophy className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Best Performing Team</p>
              <p className="text-xl font-bold mt-0.5">{data.best_performing_team.team_name}</p>
              <div className="flex flex-wrap gap-2 mt-2">
                <Badge variant="secondary">
                  <Star className="h-3 w-3 mr-1" />
                  Efficiency: {data.best_performing_team.efficiency_score}%
                </Badge>
                <Badge variant="outline">
                  <Users className="h-3 w-3 mr-1" />
                  {data.best_performing_team.agent_count} Agents
                </Badge>
                <Badge variant="outline">
                  {data.best_performing_team.total_conversations.toLocaleString()} Conversations
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Benchmark Overview */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>Avg Efficiency Score</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <Star className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {(data.benchmarks.avg_efficiency_score || 0).toFixed(1)}%
            </CardTitle>
          </CardHeader>
          <CardFooter>
            <span className="text-xs text-muted-foreground">Across all teams</span>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>Avg Resolution Time</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {Math.floor(data.benchmarks.avg_resolution_time || 0)}m
            </CardTitle>
          </CardHeader>
          <CardFooter>
            <span className="text-xs text-muted-foreground">Team average</span>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>Average CSAT Score</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <Star className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {(data.benchmarks.avg_csat || 0).toFixed(1)} / 5.0
            </CardTitle>
          </CardHeader>
          <CardFooter>
            <span className="text-xs text-muted-foreground">Customer satisfaction</span>
          </CardFooter>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-7">
        {/* Team Radar Chart */}
        <Card className="lg:col-span-4">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Team Performance Comparison (Radar)</CardTitle>
            <CardDescription>Multi-dimensional performance across key metrics</CardDescription>
          </CardHeader>
          <CardContent>
            <div style={{ height: 450 }}>
              <ResponsiveRadar
                data={data.radar_data}
                keys={data.teams.map(t => t.team_name)}
                indexBy="metric"
                valueFormat=">-.0f"
                margin={{ top: 70, right: 80, bottom: 40, left: 80 }}
                borderColor={{ from: 'color' }}
                gridLabelOffset={36}
                dotSize={10}
                dotColor={{ theme: 'background' }}
                dotBorderWidth={2}
                colors={{ scheme: 'nivo' }}
                blendMode="multiply"
                motionConfig="wobbly"
                legends={[
                  {
                    anchor: 'top-left',
                    direction: 'column',
                    translateX: -50,
                    translateY: -40,
                    itemWidth: 80,
                    itemHeight: 20,
                    itemTextColor: '#999',
                    symbolSize: 12,
                    symbolShape: 'circle',
                    effects: [
                      {
                        on: 'hover',
                        style: {
                          itemTextColor: '#000'
                        }
                      }
                    ]
                  }
                ]}
              />
            </div>
          </CardContent>
        </Card>

        {/* Efficiency Score Bar Chart */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Team Efficiency Scores</CardTitle>
            <CardDescription>Weighted: Resolution Rate (40%), Response Time (30%), CSAT (30%)</CardDescription>
          </CardHeader>
          <CardContent>
            <Chart
              options={barChartOptions}
              series={[{
                name: 'Efficiency Score',
                data: data.teams.map(t => t.efficiency_score)
              }]}
              type="bar"
              height={350}
            />
          </CardContent>
        </Card>
      </div>

      {/* Team Comparison Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Detailed Team Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rank</TableHead>
                <TableHead>Team Name</TableHead>
                <TableHead className="text-center">Agents</TableHead>
                <TableHead className="text-right">Conversations</TableHead>
                <TableHead className="text-right">Conv/Agent</TableHead>
                <TableHead className="text-right">Avg Resolution</TableHead>
                <TableHead className="text-right">Resolution Rate</TableHead>
                <TableHead className="text-right">CSAT</TableHead>
                <TableHead className="text-right">Efficiency Score</TableHead>
                <TableHead className="text-center">Performance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.teams.map((team) => (
                <TableRow key={team.team_id}>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {team.rank <= 3 && (
                        <Trophy className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className="text-sm font-bold">#{team.rank}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm font-medium">{team.team_name}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline">
                      <Users className="h-3 w-3 mr-1" />
                      {team.agent_count}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {team.total_conversations.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {team.conversations_per_agent}
                  </TableCell>
                  <TableCell className="text-right">
                    {team.avg_resolution_time}m
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <span className="text-sm">{team.resolution_rate.toFixed(1)}%</span>
                      {team.resolution_rate >= data.benchmarks.avg_efficiency_score ? (
                        <TrendingUp className="h-3 w-3 text-muted-foreground" />
                      ) : (
                        <TrendingDown className="h-3 w-3 text-muted-foreground" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Star className="h-3 w-3 text-muted-foreground" />
                      <span className="text-sm">{team.avg_csat.toFixed(1)}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="space-y-1">
                      <span className="text-sm font-bold">{team.efficiency_score.toFixed(1)}%</span>
                      <Progress value={team.efficiency_score} className="h-1.5" />
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={getPerformanceTierVariant(team.efficiency_score)}>
                      {getPerformanceTierLabel(team.efficiency_score)}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Additional Metrics */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Workload Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {data.teams.map((team) => (
                <div key={team.team_id} className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-sm">{team.team_name}</span>
                    <span className="text-sm font-medium">{team.conversations_per_agent} conv/agent</span>
                  </div>
                  <Progress value={(team.conversations_per_agent / 150) * 100} className="h-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Team Collaboration & Escalation</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Team</TableHead>
                  <TableHead className="text-right">Collaboration Index</TableHead>
                  <TableHead className="text-right">Escalation Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.teams.map((team) => (
                  <TableRow key={team.team_id}>
                    <TableCell className="text-sm">{team.team_name}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant={team.collaboration_index >= 75 ? 'secondary' : 'outline'}>
                        {team.collaboration_index}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant={team.escalation_rate <= 15 ? 'secondary' : 'outline'}>
                        {team.escalation_rate}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Performance Insights */}
      <Card className="bg-muted/30">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Performance Insights & Best Practices</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <p className="text-sm font-medium mb-2">Top Performing Teams</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>
                  <span className="text-xs text-muted-foreground">
                    <strong>{data.teams[0].team_name}</strong>: Excellent efficiency score ({data.teams[0].efficiency_score}%) — Share their practices organization-wide
                  </span>
                </li>
                <li>
                  <span className="text-xs text-muted-foreground">
                    <strong>{data.teams.find(t => t.avg_csat === Math.max(...data.teams.map(t => t.avg_csat)))?.team_name}</strong>: Highest customer satisfaction — Study their customer communication approach
                  </span>
                </li>
              </ul>
            </div>
            <div>
              <p className="text-sm font-medium mb-2">Improvement Opportunities</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>
                  <span className="text-xs text-muted-foreground">Teams with high escalation rates need additional training or resources</span>
                </li>
                <li>
                  <span className="text-xs text-muted-foreground">Balance workload across teams — some agents are handling 60% more conversations</span>
                </li>
                <li>
                  <span className="text-xs text-muted-foreground">Improve collaboration index in teams below 70% through cross-training</span>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamPerformanceAnalytics;
